import React, { useState, useEffect, useRef } from "react";
import { MSG } from "../constants/toastMessages";
import { toast } from 'react-toastify';
import { apiCallingPost } from "../api/services";
import RegexPattern from "../utils/regexPattern";
import Select from 'react-select';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { MdKeyboardArrowDown, MdVisibility, MdVisibilityOff } from "react-icons/md";
import CreatableSelect from "react-select/creatable";

const phoneUtil = PhoneNumberUtil.getInstance();

function SignupForm(props) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [mobilenumber, setMobilenumber] = useState("");
  const [emailId, setemailId] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [saveFlag, setSaveFlag] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [countryList, setCountryList] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const wrapperRef = useRef(null);
  const [companyList, setCompanyList] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [step, setStep] = useState("");


  useEffect(() => {
    getCountryList();
    getCompanyList();
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSearch(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getCountryList = () => {
    apiCallingPost("api/common/get_country").then((data) => {
      if (data.data.success === "1") {
        const countries = data.data.result || [];
        setCountryList(countries);

        // Default select Qatar if available
        const qatar = countries.find(item => item.phone_code === "+974");
        if (qatar) setSelectedCountry(qatar);
      } else {
        toast.error(data.data.successMessage);
      }
    }).catch((error) => {
      toast.error(MSG.genericError);
    });
  };
  const getCompanyList = () => {
    apiCallingPost("api/common/get_all_companies").then((data) => {
      if (data.data.success === "1") {
        const companies = data.data.result.companies || [];
        const formatted = companies.map((c) => ({ value: c, label: c }));
        setCompanyList(formatted);

      } else {
        toast.error(data.data.successMessage);
      }
    }).catch((error) => {
      toast.error(MSG.genericError);
    });
  };
  const isPhoneValid = (number, country) => {
    if (!country || !number) return false;
    try {
      const fullNumber = `${country.phone_code}${number}`;
      const phoneNumber = phoneUtil.parseAndKeepRawInput(fullNumber, country.country_code);
      return phoneUtil.isValidNumber(phoneNumber);
    } catch (error) {
      return false;
    }
  };

  const handleSignupForm = async (e) => {
    e.preventDefault();
    if (!firstName.trim()) {
      toast.error(MSG.firstNameRequired);
      document.getElementById("firstName").focus();
      return;
    }

    if (!lastName.trim()) {
      toast.error(MSG.lastNameRequired);
      document.getElementById("lastName").focus();
      return;
    }

    if (!selectedCountry) {
      toast.error(MSG.countryRequired);
      setShowSearch(true);
      return;
    }

    if (!mobilenumber || !isPhoneValid(mobilenumber, selectedCountry)) {
      toast.error(MSG.mobileInvalid);
      document.getElementById("mobilenumber").focus();
      return;
    }

    if (!emailId || !RegexPattern.Email.test(emailId)) {
      toast.error(MSG.emailInvalid);
      document.getElementById("emailId").focus();
      return;
    }

    if (!password) {
      toast.error(MSG.passwordRequired);
      document.getElementById("password").focus();
      return;
    }

    if (password.length < 6) {
      toast.error(MSG.passwordShort);
      document.getElementById("password").focus();
      return;
    }

    if (!selectedCompany?.value) {
      toast.error(MSG.companyRequired);
      return;
    }
    try {
      setSaveFlag(true);
      const milliseconds = new Date().getTime();
      const fullName = `${firstName.trim()} ${lastName.trim()}`;
      const postData = {
        display_name: fullName,
        mobile_number: mobilenumber,
        email_id: emailId,
        password: password,
        created_on: milliseconds,
        role_id: 3,
        country_id: selectedCountry?.id || "",
        company: selectedCompany?.value || ""
      };
      const data = await apiCallingPost("api/authentication/userSignup", postData);
      if (data.data.success === "1") {
        // One request creates the account AND its login identity (2026-10-25; the
        // separate public create_idp_user call is gone). The user must verify their
        // email (link sent by userSignup) before they can log in; MFA setup happens at
        // first login.
        setStep("verify-sent");
      } else {
        toast.error(data.data.errorMessage);
      }
    } catch (error) {
      console.log("🚀 ~ Signup ~ error:", error)
      toast.error(MSG.signupFailed);
    } finally {
      setSaveFlag(false);
    }
  };

  const customFilter = (option, inputValue) => {
    return (
      option.data.name.toLowerCase().includes(inputValue.toLowerCase()) ||
      option.data.phone_code.includes(inputValue)
    );
  };

  const handleChange = (selected) => {
    setSelectedCompany(selected);
  };

  const handleInputChange = (inputValue, { action }) => {
    if (action === "input-change") {
      // while typing, store as selected
      setSelectedCompany({ value: inputValue, label: inputValue });
    }
  };
  // Countdown for the resend cooldown.
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const resendVerification = async () => {
    if (resendCooldown > 0 || saveFlag) return;
    try {
      setSaveFlag(true);
      setResendCooldown(60); // throttle re-sends
      const data = await apiCallingPost("api/authentication/resendVerification", { emaild: emailId });
      if (data.data.success === "1") {
        toast.success(data.data.successMessage || "Verification email sent.");
      } else {
        toast.error(data.data.errorMessage || "Could not resend. Please try again.");
      }
    } catch (e) {
      toast.error(MSG.genericError);
      console.error("🚀 ~ resendVerification ~ error:", e);
    } finally {
      setSaveFlag(false);
    }
  };
  return (
    <>
      <div className="right_login">
        <div className="right_login_inner">
          <h2 className="right_loginhead">Sign up</h2>
          <div className="py-3 signup-sub-text">
            Sign up now and get access to exclusive content!
          </div>

          <form onSubmit={handleSignupForm}>
            <div className="d-md-flex gap-3">
              <div className="form_div" style={{ flex: 1 }}>
                <input
                  type="text"
                  id="firstName"
                  placeholder="First Name"
                  aria-label="First Name"
                  autoComplete="given-name"
                  className="form_login"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              <div className="form_div" style={{ flex: 1 }}>
                <input
                  type="text"
                  id="lastName"
                  placeholder="Last Name"
                  aria-label="Last Name"
                  autoComplete="family-name"
                  className="form_login"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
            </div>

            <div className="form_div" ref={wrapperRef}>
              <div className="d-flex align-items-center gap-2 form-flex-class">
                <div
                  className="form-select-sign"
                  role="button"
                  tabIndex={0}
                  aria-haspopup="listbox"
                  aria-expanded={showSearch}
                  aria-label={`Country code, currently ${selectedCountry?.phone_code || "none selected"}`}
                  onClick={() => setShowSearch(!showSearch)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setShowSearch((s) => !s);
                    } else if (e.key === "Escape") {
                      setShowSearch(false);
                    }
                  }}
                >
                  {selectedCountry?.phone_code}
                  <MdKeyboardArrowDown />
                </div>
                {showSearch && (
                  <div className="menuIsOpen-select-country">
                    <Select
                      classNamePrefix="select"
                      id="countrySelect"
                      isSearchable
                      options={countryList}
                      value={selectedCountry}
                      onChange={(selectedOption) => {
                        setSelectedCountry(selectedOption);
                        setShowSearch(false);
                      }}
                      getOptionValue={(option) => option.id}
                      getOptionLabel={(option) => `${option.name}`}
                      filterOption={customFilter}
                      placeholder="Search country code..."
                      noOptionsMessage={({ inputValue }) =>
                        inputValue ? `No country codes found for "${inputValue}"` : "No country codes available"
                      }
                      menuIsOpen
                      autoFocus
                    />
                  </div>
                )}
                <input
                  type="tel"
                  id="mobilenumber"
                  placeholder="Mobile Number"
                  aria-label="Mobile Number"
                  autoComplete="tel-national"
                  className="form_login mb-0"
                  value={mobilenumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    setMobilenumber(value);
                  }}
                  inputMode="numeric"
                />
              </div>
            </div>

            <div className="form_div">
              <input
                type="email"
                id="emailId"
                placeholder="Email Id"
                aria-label="Email address"
                autoComplete="email"
                value={emailId}
                onChange={(e) => setemailId(e.target.value)}
                className="form_login"
              />
            </div>

            <div className="form_div" style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                placeholder="Password"
                aria-label="Password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="form_login"
              />
              <span
                onClick={() => setShowPassword((s) => !s)}
                role="button"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                title={showPassword ? 'Hide password' : 'Show password'}
                style={{ position: 'absolute', right: 14, top: 'calc(50% - 10px)', transform: 'translateY(-50%)', cursor: 'pointer', color: '#666', display: 'flex' }}
              >
                {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
              </span>
            </div>
            <small className="d-block mb-2" style={{ fontSize: '12px', marginTop: '-6px', color: 'rgba(255,255,255,0.75)' }}>
              Use at least 6 characters.
            </small>
            <div className="form_div mb-3">
              <CreatableSelect
                className="creatable-select"
                options={companyList}
                value={selectedCompany}
                onChange={handleChange}
                onInputChange={handleInputChange}
                placeholder="Select or Type company name"
                isClearable={false}
                isSearchable
                classNamePrefix="select"
                formatCreateLabel={(inputValue) => `${inputValue}`} // custom text when typing
              />
            </div>
            <p className="forgot-pwd-sec text-end">
              <span className="signup-text">
                Already Registered?{" "}
                <span className="ms-2 cursor-pointer" onClick={() => props.showSignup(false)}>
                  Back to Sign In
                </span>
              </span>
            </p>

            <button
              type="submit"
              disabled={saveFlag}
              className="login_btn"
            >
              {saveFlag ? "Signing Up..." : "Sign Up"}
            </button>
          </form>
          <div className="py-3 privacy-text">
            By clicking on Sign up, you agree to our Terms of service and Privacy policy.
          </div>

          <div className="copyright">
            © {new Date().getFullYear()} Visit Qatar. All rights reserved.
          </div>
        </div>
      </div>
      {step === "verify-sent" && (
        <div className="popup-outer">
          <div className="popup-inner">
            <div className="popup-header">Verify your email</div>
            <div className="popup-body">
              <p className="mb-2">We've sent a verification link to <strong>{emailId}</strong>.</p>
              <p className="mb-2">Open it to activate your account, then sign in. Didn't get it? Check your spam folder.</p>
              <p className="mb-3 text-muted" style={{ fontSize: '13px' }}>The link expires in 24 hours.</p>
              <button
                type="button"
                className="btn btn-primary scanner-btn w-100"
                onClick={resendVerification}
                disabled={saveFlag || resendCooldown > 0}
              >
                {saveFlag ? "Sending…" : resendCooldown > 0 ? `Resend email (${resendCooldown}s)` : "Resend email"}
              </button>
              <p className="mt-3 mb-0" style={{ fontSize: '13px' }}>
                <span className="cursor-pointer" style={{ textDecoration: 'underline' }} onClick={() => { setStep(""); }}>
                  Wrong email address? Go back
                </span>
              </p>
              <p className="mt-2 mb-0" style={{ fontSize: '13px' }}>
                <span className="cursor-pointer" style={{ textDecoration: 'underline' }} onClick={() => { setStep(""); props.showSignup(false); }}>
                  Back to Sign In
                </span>
              </p>
            </div>
          </div>
        </div>
      )}

    </>
  );
}

export default SignupForm;
